import { useStoreState } from '@ariakit/react';
import { Popover, PopoverDisclosure, PopoverHeading, usePopoverStore } from '@ariakit/react/popover';
import cn from 'classnames';
import { useMemo } from 'react';
import { useSuspenseTagsQuery } from '#api/tags.queries.ts';
import { TagPill } from '#components/atoms/pill/tag-pill/TagPill.tsx';
import { Tooltip } from '#components/atoms/tooltip/Tooltip.tsx';
import { TagLink } from '#components/patterns/entity-links/tag-link/TagLink.tsx';
import type { TagName } from '#schemas/tag/name.ts';
import styles from './InlineTagsList.module.css';

type Props = {
  className?: string;
  tagNames: TagName[];
  /** Number of tags to show before truncating. If undefined, the entire list will be shown */
  truncateAfter?: number;
  /** Link each pill to its tag detail page. Deleted tags have no page, so they stay inert. */
  linkTags?: boolean;
};

export function InlineTagsList({ className, tagNames, truncateAfter, linkTags }: Props) {
  const tags = useSuspenseTagsQuery(tagNames).data.data;
  const knownTagNames = useMemo(() => new Set(tags.map(({ name }) => name)), [tags]);
  // Maintain original order: known tags first (sorted by server order), then deleted tags
  const sortedTagNames = useMemo(() => {
    const known = tags.map(({ name }) => name);
    const deleted = tagNames.filter((name) => !knownTagNames.has(name));
    return [...known, ...deleted];
  }, [tags, tagNames, knownTagNames]);

  if (!tagNames.length) {
    return null;
  }

  const shownTags = !truncateAfter ? sortedTagNames : sortedTagNames.slice(0, truncateAfter);
  const extraTags = !truncateAfter ? [] : sortedTagNames.slice(truncateAfter);

  return (
    <ul className={cn(styles.InlineTagsList_tagPills, 'list-inline flex gap-8', className)}>
      {shownTags.map((tagName) => {
        const isKnown = knownTagNames.has(tagName);

        // Linked pills drop the tooltip (the popover handles discovery); unlinked pills keep it, as it
        // reveals truncated names and is the only non-color cue that a tag was deleted (WCAG 1.4.1).
        return (
          <li className="mw-0" key={tagName}>
            {linkTags && isKnown ?
              <TagLink className={styles.InlineTagsList_tagLink} tagName={tagName}>
                <TagPill className={styles.InlineTagsList_tagPill} label={tagName} />
              </TagLink>
            : <Tooltip content={isKnown ? tagName : `${tagName} (deleted)`}>
                <TagPill
                  className={styles.InlineTagsList_tagPill}
                  label={tagName}
                  variant={isKnown ? undefined : 'alert'}
                />
              </Tooltip>
            }
          </li>
        );
      })}
      {extraTags.length ?
        <li>
          {linkTags ?
            <TagsOverflowPopover
              tagNames={sortedTagNames}
              knownTagNames={knownTagNames}
              extraCount={extraTags.length}
            />
          : <Tooltip content={extraTags.join(', ')}>
              <TagPill
                className={cn(styles.InlineTagsList_tagPill, styles.InlineTagsList_truncator)}
                label={`+${extraTags.length}`}
              />
            </Tooltip>
          }
        </li>
      : null}
    </ul>
  );
}

type TagsOverflowPopoverProps = {
  /** Every tag on the entity, shown in full inside the popover. */
  tagNames: TagName[];
  knownTagNames: Set<TagName>;
  /** How many tags are hidden behind the `+N` trigger. */
  extraCount: number;
};

/** The `+N` truncator, expanded into a clickable popover that lists every tag as a link. */
function TagsOverflowPopover({ tagNames, knownTagNames, extraCount }: TagsOverflowPopoverProps) {
  const popover = usePopoverStore({ placement: 'bottom-start' });
  const isOpen = useStoreState(popover, 'open');

  return (
    <>
      <PopoverDisclosure
        store={popover}
        className={cn(styles.InlineTagsList_truncatorButton, { [styles.InlineTagsList_truncatorButtonOpen]: isOpen })}
        aria-label={`Show all ${tagNames.length} tags`}
      >
        <TagPill
          className={cn(styles.InlineTagsList_tagPill, styles.InlineTagsList_truncator)}
          label={`+${extraCount}`}
        />
      </PopoverDisclosure>
      <Popover store={popover} portal={true} gutter={6} className={styles.InlineTagsList_popover}>
        {/* Unrender when not open, to clear the state. */}
        {isOpen ?
          <>
            <PopoverHeading
              className={cn('eyebrow', styles.InlineTagsList_popoverHeader)}
              render={<h3>All {tagNames.length} tags</h3>}
            />
            <ul className={cn('list-inline flex flex-wrap gap-8', styles.InlineTagsList_popoverList)}>
              {tagNames.map((tagName) => {
                const isKnown = knownTagNames.has(tagName);

                // The popover has no hover surface, so deleted tags spell out their state in the label.
                return (
                  <li className="mw-0" key={tagName}>
                    {isKnown ?
                      <TagLink className={styles.InlineTagsList_tagLink} tagName={tagName} onClick={popover.hide}>
                        <TagPill className={styles.InlineTagsList_tagPill} label={tagName} />
                      </TagLink>
                    : <TagPill
                        className={styles.InlineTagsList_tagPill}
                        label={`${tagName} (deleted)`}
                        variant="alert"
                      />
                    }
                  </li>
                );
              })}
            </ul>
          </>
        : null}
      </Popover>
    </>
  );
}
